if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.dataset.stickyAtcBound === 'true') return;

        this.dataset.stickyAtcBound = 'true';
        this.displayMode = this.dataset.displayMode || 'after_scroll';
        this.displayThreshold = Number(this.dataset.displayThreshold || 0);
        this.sectionId = this.dataset.section || '';
        this.onScroll = this.onScroll.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);

        this.addEventListener('click', this.onClick);
        document.addEventListener('variant:change', this.onVariantChange);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.updateVisibility);

        this.updateVisibility();
        this.syncHeightPadding();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('variant:change', this.onVariantChange);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.updateVisibility);
        document.body.classList.remove('sticky-atc-bottom-visible');
      }

      onScroll() {
        window.requestAnimationFrame(this.updateVisibility);
      }

      onClick(event) {
        const scrollButton = event.target.closest('.sticky-atc__scroll-btn');
        if (scrollButton || this.dataset.scrollBtn === 'true') {
          const button = scrollButton || event.target.closest('button');
          if (!button) return;

          event.preventDefault();
          this.scrollToDestination();
          return;
        }

        const addButton = event.target.closest('.sticky-atc__add_button');
        if (!addButton || addButton.matches('a')) return;

        event.preventDefault();
        if (addButton.disabled || addButton.getAttribute('aria-disabled') === 'true') return;

        const form = this.getProductForm();
        const submitButton = this.getMainSubmitButton(form);
        if (submitButton) {
          submitButton.click();
          return;
        }

        form?.requestSubmit?.();
      }

      onVariantChange(event) {
        if (event.target?.dataset?.section !== this.sectionId) return;

        const variant = event.detail?.variant;
        if (!variant) return;

        this.updateImage(variant);
        this.updateButtonState(variant);
      }

      updateVisibility() {
        const shouldShow = this.shouldShow();

        this.classList.toggle('sticky-atc--visible', shouldShow);
        document.body.classList.toggle('sticky-atc-bottom-visible', shouldShow);
        this.syncHeightPadding();
      }

      shouldShow() {
        if (this.displayMode === 'always') return true;

        const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;

        if (this.displayMode === 'after_pixels') return scrollTop >= this.displayThreshold;

        if (this.displayMode === 'after_percent') {
          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollable <= 0) return false;
          return (scrollTop / scrollable) * 100 >= this.displayThreshold;
        }

        return this.isPastPrimaryAtc();
      }

      isPastPrimaryAtc() {
        const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
        if (scrollTop < 20) return false;

        const primaryButton = this.getPrimaryAtcTrigger();
        if (!primaryButton) return scrollTop > 120;

        const rect = primaryButton.getBoundingClientRect();
        if (!rect.width && !rect.height) return scrollTop > 120;

        return rect.bottom < 0 || rect.top > window.innerHeight;
      }

      getPrimaryAtcTrigger() {
        return (
          document.querySelector(`#ProductSubmitButton-${this.cssEscape(this.sectionId)}`) ||
          document.querySelector(`#ProductForm-${this.cssEscape(this.sectionId)} .product-form__buttons`) ||
          document.querySelector(`#ProductForm-${this.cssEscape(this.sectionId)}`) ||
          document.querySelector(`#product-form-${this.cssEscape(this.sectionId)}`)
        );
      }

      getProductForm() {
        return document.querySelector(`#product-form-${this.cssEscape(this.sectionId)}`);
      }

      getMainSubmitButton(form) {
        if (!form) return null;

        return form.querySelector('[type="submit"][name="add"]');
      }

      scrollToDestination() {
        const selector = this.dataset.scrollDestination;
        if (!selector) return;

        const normalizedSelector = selector.replace('-id', `-${this.sectionId}`);
        const destination = document.querySelector(normalizedSelector);
        destination?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      updateImage(variant) {
        const image = this.querySelector(`#sticky-atc-image-${this.cssEscape(this.sectionId)}`);
        const source = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
        if (!image || !source) return;

        image.src = source;
        image.alt = variant.featured_image?.alt || variant.featured_media?.alt || image.alt || '';
      }

      updateButtonState(variant) {
        const button = this.querySelector('.sticky-atc__add_button');
        if (!button) return;

        const unavailable = !variant.available;
        button.toggleAttribute('disabled', unavailable);
        button.toggleAttribute('aria-disabled', unavailable);

        const label = button.querySelector('.sticky-atc__label__text');
        if (!label) return;

        if (unavailable) {
          label.textContent = window.variantStrings?.soldOut || 'Sold out';
        } else {
          label.textContent = button.dataset.label || window.variantStrings?.addToCart || 'Add to cart';
        }
      }

      syncHeightPadding() {
        const height = this.classList.contains('sticky-atc--visible') ? Math.ceil(this.getBoundingClientRect().height) : 0;
        document.documentElement.style.setProperty('--sticky-atc-height', `${height}px`);
      }

      cssEscape(value) {
        if (window.CSS?.escape) return window.CSS.escape(value);
        return String(value).replace(/"/g, '\\"');
      }
    }
  );
}

if (!customElements.get('secondary-variant-select') || !customElements.get('secondary-variant-select-separate')) {
  class StickySecondaryVariantSelect extends HTMLElement {
    connectedCallback() {
      if (this.dataset.stickySecondaryVariantBound === 'true') return;

      this.dataset.stickySecondaryVariantBound = 'true';
      this.addEventListener('change', this.onChange.bind(this));
      document.addEventListener('variant:change', this.onVariantChange.bind(this));
    }

    onChange(event) {
      if (!event.target.matches('select')) return;

      const variant = this.getSelectedVariant();
      if (!variant) return;

      const syncedMainPicker = this.syncMainPicker(variant);
      this.syncForms(variant);
      if (!syncedMainPicker) {
        this.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: { variant },
          })
        );
      }
    }

    onVariantChange(event) {
      if (event.target === this || event.target?.dataset?.section !== this.dataset.section) return;

      const variant = event.detail?.variant;
      if (!variant) return;

      this.setSelectedVariant(variant);
    }

    getSelectedVariant() {
      const variants = this.getVariantData();
      if (!variants.length) return null;

      const combinedSelect = this.querySelector('.variant-dropdown');
      if (combinedSelect) {
        const variantId = Number(combinedSelect.selectedOptions[0]?.dataset.variantId);
        return variants.find((variant) => Number(variant.id) === variantId) || null;
      }

      const selectedOptions = Array.from(this.querySelectorAll('.product-form__input select')).map((select) => select.value);
      return variants.find((variant) => variant.options.every((option, index) => option === selectedOptions[index])) || null;
    }

    setSelectedVariant(variant) {
      const combinedSelect = this.querySelector('.variant-dropdown');
      if (combinedSelect) {
        const option = Array.from(combinedSelect.options).find((item) => Number(item.dataset.variantId) === Number(variant.id));
        if (option) combinedSelect.value = option.value;
        return;
      }

      this.querySelectorAll('.product-form__input select').forEach((select, index) => {
        if (variant.options[index] !== undefined) select.value = variant.options[index];
      });
    }

    syncMainPicker(variant) {
      const mainPicker =
        document.querySelector(`variant-selects[data-section="${this.cssEscape(this.dataset.section)}"]`) ||
        document.querySelector(`variant-radios[data-section="${this.cssEscape(this.dataset.section)}"]`);

      if (!mainPicker) return false;

      variant.options.forEach((optionValue, index) => {
        const optionGroup = mainPicker.querySelectorAll('.product-form__input')[index];
        if (!optionGroup) return;

        const select = optionGroup.querySelector('select');
        if (select) {
          select.value = optionValue;
          return;
        }

        const radio = Array.from(optionGroup.querySelectorAll('input[type="radio"]')).find((input) => input.value === optionValue);
        if (radio) radio.checked = true;
      });

      mainPicker.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    syncForms(variant) {
      const section = this.dataset.section;
      if (!section) return;

      document.querySelectorAll(`#product-form-${this.cssEscape(section)}, #product-form-installment-${this.cssEscape(section)}, product-form[data-section="${this.cssEscape(section)}"] form`).forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;

        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    getVariantData() {
      try {
        const json = this.querySelector('script[type="application/json"]')?.textContent;
        return json ? JSON.parse(json) : [];
      } catch (error) {
        return [];
      }
    }

    cssEscape(value) {
      if (window.CSS?.escape) return window.CSS.escape(value);
      return String(value).replace(/"/g, '\\"');
    }
  }

  if (!customElements.get('secondary-variant-select')) {
    customElements.define('secondary-variant-select', StickySecondaryVariantSelect);
  }

  if (!customElements.get('secondary-variant-select-separate')) {
    customElements.define('secondary-variant-select-separate', StickySecondaryVariantSelect);
  }
}
